import { jsx, jsxs } from "react/jsx-runtime";
import { useThemeName, Strong, Text, IconGift, IconPromote, Box, ContentBlock } from "braid-design-system";
import { useTranslations } from "@vocab/react";
import { useEffect, useContext, useCallback } from "react";
import { translations } from "./.vocab/index.mjs";
import * as Apollo from "@apollo/client";
import { gql, useApolloClient } from "@apollo/client";
import { ShoppingCartContext } from "@seek/shopping-cart-provider";
const sendTealiumEvent = ({
  code,
  campaignId,
  discountRate,
  tealiumProductDisplayNames,
  bannerProductDisplayNames,
  onTrackingCallback
}) => {
  const tealiumPromoCodeText = discountRate === 100 ? `Post a ${bannerProductDisplayNames} ad for FREE.` : `Get ${discountRate}% off a ${bannerProductDisplayNames} ad.`;
  const alertShownMessageCode = [
    tealiumPromoCodeText,
    `Use promocode ${code} at checkout.`
  ].join(" ");
  try {
    if (onTrackingCallback) {
      onTrackingCallback({
        eventName: "alert_viewed",
        alertShownTone: "promote",
        alertShownType: "alert",
        alertShownPosition: "header",
        alertShownTitle: `PromoCode_${campaignId}_${code}_${discountRate}_${tealiumProductDisplayNames}`,
        alertShownMessageCode
      });
    }
  } catch (e) {
    console.error({
      message: "Promo code banner tealium.link() error",
      error: e
    });
  }
};
const dedupeProductNames = (productNames) => [
  ...new Set(productNames)
];
const BannerContent = ({
  promoCode,
  onTrackingCallback
}) => {
  const {
    code,
    campaignId,
    discount: { discountRate },
    validProducts
    // validProducts includes id & displayName
  } = promoCode;
  const { t } = useTranslations(translations);
  const theme = useThemeName();
  const productDisplayNames = dedupeProductNames(
    validProducts.map((product) => product.displayName)
  );
  const createBannerProductDisplayNames = (displayNames) => {
    if (displayNames.length === 1) return displayNames[0];
    if (displayNames.length === 2) return displayNames.join(` ${t("or")} `);
    const lastDisplayName = displayNames[displayNames.length - 1];
    const restOfDisplayNames = displayNames.slice(0, -1);
    const joinedDisplayNames = restOfDisplayNames.join(", ");
    return `${joinedDisplayNames} ${t("or")} ${lastDisplayName}`;
  };
  const bannerProductDisplayNames = createBannerProductDisplayNames(productDisplayNames);
  const discountRatePromoCodeText = t("Get discountRate off", {
    validProductDisplayNames: bannerProductDisplayNames,
    Off: () => /* @__PURE__ */ jsx(Strong, { children: t("off") }),
    DiscountRate: () => /* @__PURE__ */ jsx(Strong, { children: `${discountRate.toString()}%` })
  });
  const freePromoCodeText = t("Post Free Ad", {
    validProductDisplayNames: bannerProductDisplayNames,
    Free: () => /* @__PURE__ */ jsx(Strong, { children: t("FREE") })
  });
  const usePromoCodeText = t("Use promocode at checkout", {
    Code: () => /* @__PURE__ */ jsx(Strong, { children: code })
  });
  const tealiumProductDisplayNames = productDisplayNames.join("");
  useEffect(() => {
    sendTealiumEvent({
      code,
      campaignId,
      discountRate,
      tealiumProductDisplayNames,
      bannerProductDisplayNames,
      onTrackingCallback
    });
  }, [
    bannerProductDisplayNames,
    code,
    campaignId,
    discountRate,
    tealiumProductDisplayNames,
    onTrackingCallback
  ]);
  return /* @__PURE__ */ jsxs(Text, { icon: theme === "apac" ? /* @__PURE__ */ jsx(IconGift, {}) : /* @__PURE__ */ jsx(IconPromote, {}), children: [
    discountRate === 100 ? freePromoCodeText : discountRatePromoCodeText,
    " ",
    usePromoCodeText
  ] });
};
const defaultOptions = {};
const PromoCodesDocument = gql`
    query PromoCodes {
  promoCodes {
    code
    campaignId
    discount {
      ... on PromoCodeDiscountRate {
        discountRate
      }
    }
    createdAt
    updatedAt
    validFrom
    validTo
    validProducts {
      id
      displayName
    }
  }
}
    `;
function usePromoCodesQuery(baseOptions) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useQuery(PromoCodesDocument, options);
}
const hasValidProducts = (promoCode) => promoCode && promoCode.validProducts.length > 0;
const sortByLatestDate = (promoCodes) => {
  const promoCodesToSort = [...promoCodes];
  promoCodesToSort.sort((a, b) => {
    const aDate = new Date(a.createdAt);
    const bDate = new Date(b.createdAt);
    return bDate.getTime() - aDate.getTime();
  });
  return promoCodesToSort;
};
const applicablePromoCodes = (productIds, promoCodes) => {
  if (productIds) {
    return promoCodes == null ? void 0 : promoCodes.filter(
      ({ validProducts }) => validProducts.some(({ id }) => productIds.includes(id))
    );
  }
  return promoCodes;
};
const usePromoCodesBanner = (productIds) => {
  const {
    data: promoCodesData,
    loading: promoCodesLoading,
    error: promoCodesError
  } = usePromoCodesQuery();
  const promoCodes = applicablePromoCodes(
    productIds,
    promoCodesData == null ? void 0 : promoCodesData.promoCodes
  );
  const [latestPromoCode] = sortByLatestDate(promoCodes ?? []);
  return {
    shouldRender: !promoCodesLoading && !promoCodesError && hasValidProducts(latestPromoCode),
    latestPromoCode
  };
};
const HirerPromoCodesBanner = ({
  onTrackingCallback,
  padding = {
    paddingX: { mobile: "medium", desktop: "none" },
    paddingY: ["medium"]
  },
  productIds
}) => {
  const theme = useThemeName();
  const { shouldRender, latestPromoCode } = usePromoCodesBanner(productIds);
  if (!shouldRender) {
    return null;
  }
  return /* @__PURE__ */ jsx(
    Box,
    {
      background: "promoteLight",
      borderRadius: theme === "seekJobs" ? "large" : "none",
      children: /* @__PURE__ */ jsx(ContentBlock, { children: /* @__PURE__ */ jsx(Box, { paddingX: padding.paddingX, paddingY: padding.paddingY, children: /* @__PURE__ */ jsx(
        BannerContent,
        {
          promoCode: latestPromoCode,
          onTrackingCallback
        }
      ) }) })
    }
  );
};
const isAdProduct = (product) => product.__typename === "AdProduct";
const getProductIdsFromProductSetProducts = (products) => products.flatMap((product) => {
  var _a;
  if (isAdProduct(product)) {
    const upgradeProductsIds = ((_a = product.upgradePaths) == null ? void 0 : _a.map((path) => path.targetProduct2.id)) ?? [];
    return [product.id, ...upgradeProductsIds];
  }
  return [product.id];
});
const HirerPromoCodesBannerConnectedCart = ({
  onTrackingCallback,
  padding,
  shoppingCart
}) => {
  const shoppingCartContext = useContext(ShoppingCartContext);
  const {
    subscriptionResponseKey,
    key: subscriptionUniqueIdKey,
    getProductIdsDocument,
    getProductIdsDocumentConstructVariables
  } = shoppingCart;
  const { productResponses, subscribeForProducts, unsubscribeForProducts } = shoppingCartContext;
  const client = useApolloClient();
  const getProductIdsQuery = useCallback(
    async (productSetId) => await client.query({
      query: getProductIdsDocument,
      variables: getProductIdsDocumentConstructVariables(productSetId)
    }),
    [getProductIdsDocument, getProductIdsDocumentConstructVariables, client]
  );
  useEffect(() => {
    subscribeForProducts(
      subscriptionUniqueIdKey,
      subscriptionResponseKey,
      getProductIdsQuery
    );
    return () => {
      if (typeof unsubscribeForProducts !== "undefined")
        unsubscribeForProducts(subscriptionUniqueIdKey);
    };
  }, [
    getProductIdsQuery,
    subscribeForProducts,
    subscriptionUniqueIdKey,
    subscriptionResponseKey,
    unsubscribeForProducts
  ]);
  const { products, error } = productResponses[subscriptionUniqueIdKey] ?? {};
  const productIds = getProductIdsFromProductSetProducts(
    (products == null ? void 0 : products.products) ?? []
  );
  if (!productIds.length || error) {
    return null;
  }
  return /* @__PURE__ */ jsx(
    HirerPromoCodesBanner,
    {
      onTrackingCallback,
      productIds,
      padding
    }
  );
};
export {
  HirerPromoCodesBanner,
  HirerPromoCodesBannerConnectedCart,
  usePromoCodesBanner
};
